import React, { useEffect, useState, useMemo } from "react";
import PageHeader from "../../../common/pageheader/pageheader";
import AGTable from "../../../common/table/AGTable";
import api from "../../../../resources/api";
import Loader from "../../../common/loader/loader";
import { currencyConverter, formatDateAndTime } from "../../../../resources/constants";
import { connect } from "react-redux";
import SearchSelect from "../../../common/select/SearchSelect";

function FeeTypeCollection() {
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [semesterList, setSemesterList] = useState([]);
    const [summaryStats, setSummaryStats] = useState({ GrandTotalBilled: 0, TotalCollected: 0, TotalOutstanding: 0 });
    const [filters, setFilters] = useState({ SemesterCode: "" });
    const [datatable, setDatatable] = useState({
        columns: [
            { label: "Fee Type", field: "FeeType" },
            { label: "Students", field: "StudentCount" },
            { label: "Invoices", field: "InvoiceCount" },
            { label: "Total Billed", field: "TotalBilled" },
            { label: "% of Total", field: "Percentage" },
        ],
        rows: [],
    });

    const semesterOptions = useMemo(() => semesterList.map(s => ({
        value: s.SemesterCode,
        label: (s.SemesterName || s.SemesterCode) + (s.IsCurrent === 1 ? " (Current)" : ""),
    })), [semesterList]);

    const getSemesters = async () => {
        const result = await api.get("staff/registration/semester/list");
        if (result.success && result.data) {
            const data = Array.isArray(result.data) ? result.data : result.data.data || [];
            setSemesterList(data);
            const current = data.find(s => s.IsCurrent === 1);
            setFilters(f => ({ ...f, SemesterCode: current?.SemesterCode || data[0]?.SemesterCode || "" }));
        }
    };

    const getReportData = async () => {
        if (!filters.SemesterCode) { setIsLoading(false); return; }
        setIsLoading(true);
        const result = await api.get(`staff/finance/invoices/fee-type-collection?semester=${filters.SemesterCode}`);
        if (result.success && result.data?.data) {
            const { summary, rows } = result.data.data;
            setSummaryStats(summary || {});
            setReportData(rows || []);
            buildTable(rows || [], summary || {});
        }
        setIsLoading(false);
    };

    const buildTable = (data, summary) => {
        const grandTotal = parseFloat(summary.GrandTotalBilled) || 0;
        const rows = data.map((item) => ({
            FeeType: <span className="badge badge-light-primary">{item.FeeType}</span>,
            StudentCount: item.StudentCount,
            InvoiceCount: item.InvoiceCount,
            TotalBilled: <span className="text-success fw-bold">{currencyConverter(item.TotalBilled)}</span>,
            Percentage: (
                <span className="fw-bold">
                    {grandTotal > 0 ? ((parseFloat(item.TotalBilled) / grandTotal) * 100).toFixed(1) : "0.0"}%
                </span>
            ),
        }));
        setDatatable(prev => ({ ...prev, rows }));
    };

    const exportToCSV = () => {
        if (!reportData.length) return;
        const grandTotal = parseFloat(summaryStats.GrandTotalBilled) || 0;
        const headers = ["Fee Type", "Students", "Invoices", "Total Billed", "% of Total"];
        const rows = reportData.map(item => [
            item.FeeType, item.StudentCount, item.InvoiceCount, item.TotalBilled,
            grandTotal > 0 ? ((parseFloat(item.TotalBilled) / grandTotal) * 100).toFixed(1) + "%" : "0.0%",
        ]);
        const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `fee_type_collection_${filters.SemesterCode}_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
    };

    const collectionRate = () => {
        const billed = parseFloat(summaryStats.GrandTotalBilled) || 0;
        const collected = parseFloat(summaryStats.TotalCollected) || 0;
        return billed > 0 ? ((collected / billed) * 100).toFixed(1) : "0.0";
    };

    useEffect(() => { getSemesters(); }, []);
    useEffect(() => { if (filters.SemesterCode) getReportData(); }, [filters.SemesterCode]);

    return isLoading ? <Loader /> : (
        <div className="d-flex flex-column flex-row-fluid">
            <PageHeader title="Fee-Type Collection" items={["Finance", "Invoices", "Fee-Type Collection"]} />
            <div className="flex-column-fluid">
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-5">
                                <label className="form-label required">Semester</label>
                                <SearchSelect
                                    id="SemesterCode"
                                    value={semesterOptions.find(o => o.value === filters.SemesterCode) || null}
                                    options={semesterOptions}
                                    onChange={(s) => setFilters(f => ({ ...f, SemesterCode: s?.value || "" }))}
                                    placeholder="Select Semester"
                                    isClearable={false}
                                />
                            </div>
                            <div className="col-md-7 text-end">
                                <button className="btn btn-light-primary" onClick={exportToCSV} disabled={!reportData.length}>
                                    <i className="fa fa-download me-2"></i>Export CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {filters.SemesterCode && (
                    <>
                        <div className="row g-4 mb-4">
                            {[
                                { label: "Total Billed", value: summaryStats.GrandTotalBilled, color: "primary", icon: "fa-file-invoice" },
                                { label: "Total Collected", value: summaryStats.TotalCollected, color: "success", icon: "fa-money-bill-wave" },
                                { label: "Total Outstanding", value: summaryStats.TotalOutstanding, color: "danger", icon: "fa-exclamation-circle" },
                                { label: "Collection Rate", value: `${collectionRate()}%`, color: "warning", icon: "fa-chart-pie", isText: true },
                            ].map((c, i) => (
                                <div className="col-md-3" key={i}>
                                    <div className={`card bg-light-${c.color}`}>
                                        <div className="card-body py-4">
                                            <div className="d-flex align-items-center">
                                                <div className="symbol symbol-50px me-3">
                                                    <span className={`symbol-label bg-${c.color}`}>
                                                        <i className={`fa ${c.icon} text-white fs-3`}></i>
                                                    </span>
                                                </div>
                                                <div>
                                                    <div className="fs-5 fw-bold">{c.isText ? c.value : currencyConverter(c.value || 0)}</div>
                                                    <div className="text-muted small">{c.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="alert alert-warning d-flex align-items-center mb-4">
                            <i className="fa fa-info-circle me-3"></i>
                            <span>Total Collected reflects invoice-level payments. Total Billed is from fee line items.</span>
                        </div>

                        <div className="card">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Fee Type Breakdown</h3>
                                <div className="card-toolbar">
                                    <span className="text-muted fs-7">{reportData.length} fee types</span>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                {reportData.length > 0 ? <AGTable data={datatable} /> : (
                                    <div className="text-center py-10">
                                        <p className="text-muted">No fee data for this semester</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default connect()(FeeTypeCollection);
